import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Truck, Shield, LogOut, User as UserIcon, 
  TruckIcon, Settings, MessageSquare, Navigation, UserCheck, 
  Handshake, UserCog, Bell, ChevronDown, ChevronRight, FileText, FileSearch, AlertTriangle,
  FileSignature, Database, Building2, Cpu, PanelLeftClose, PanelLeft, X, Menu, Target, BookOpen,
  ShieldAlert, Container, ClipboardCheck, Edit3, Sparkles, Heart, HeartHandshake, Eye,
  Trophy, Wallet, Gauge, Send, Landmark, Link2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useUnreadMessagesCount } from '../hooks/useUnreadMessagesCount';
import { useWavePendingCount } from '../hooks/useWavePendingCount';
import { cn } from '../lib/utils';
import axios from 'axios';

const AdminSidebar = ({ collapsed, onToggle, mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const { hasPermission, hasAnyPermission, isAdmin, loading: permsLoading } = usePermissions();
  const { count: unreadMessages, assignedCount: unreadAssigned, othersCount: unreadOthers } = useUnreadMessagesCount();
  const wavePending = useWavePendingCount({ enabled: hasPermission ? hasPermission('wave.view') : false });
  const [pendingAccessRequests, setPendingAccessRequests] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(() => {
    const settingsPaths = [
      '/admin/settings',
      '/admin/backup',
      '/admin/unified-users',
      '/admin/notifications/templates',
      '/admin/payment-policies',
      '/admin/system-config',
      '/admin/roles',
      '/admin/mcp'
    ];
    return settingsPaths.some(p => window.location.pathname.startsWith(p));
  });
  // Groupes dépliables des sections métier (ex. Rapprochement > Wave)
  const [openGroups, setOpenGroups] = useState({});

  const isActive = (path, exact = false) => {
    // Handle paths with query params (e.g., /admin/settings?tab=payment)
    if (path.includes('?')) {
      const [basePath, query] = path.split('?');
      const params = new URLSearchParams(query);
      const searchParams = new URLSearchParams(location.search);
      return location.pathname === basePath && params.get('tab') === searchParams.get('tab');
    }
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // V2 RBAC — Sidebar par sections métier (perm conservées ; null = visible pour tous)
  // Dashboard, Pilotage commercial et Pilotage opérations sont gérés séparément (en tête).
  const mainSections = [
    { label: 'Finance', items: [
      { icon: Sparkles, label: 'Cockpit CFO', path: '/admin/finance/cockpit', perm: 'finance.cockpit.view' },
      { icon: FileSearch, label: 'Rapprochement (unifié)', path: '/admin/rapprochement', anyPerm: ['wave.view', 'bank.view', 'finance.cockpit.view'], badge: null },
      { icon: Shield, label: 'Factures FNE', path: '/admin/fne', anyPerm: ['fne.invoices.view', 'fne.invoices.certify', 'finance.audit.view'] },
      { icon: Wallet, label: 'Wave', children: [
        { icon: FileSearch, label: 'Rapprochement', path: '/admin/rapprochement/wave', exact: true, anyPerm: ['wave.view', 'finance.cockpit.view'], badge: 'wavePending' },
        { icon: Send, label: 'Liste des paiements', path: '/admin/rapprochement/wave/paiements', perm: 'payment_requests.view' },
        { icon: Link2, label: 'Liens de paiement', path: '/admin/rapprochement/wave/liens', anyPerm: ['wave.view', 'finance.cockpit.view'] },
      ] },
      { icon: Landmark, label: 'Banque', path: '/admin/rapprochement/banque', anyPerm: ['wave.view', 'finance.cockpit.view'] },
    ] },
    { label: 'Relation client', items: [
      { icon: HeartHandshake, label: 'Customer Success', path: '/admin/customer-success', perm: 'customer_success.view' },
      { icon: MessageSquare, label: 'Messagerie', path: '/admin/messagerie', badge: 'unreadMessages', perm: 'messaging.view.all' },
      { icon: Building2, label: 'Clients', path: '/admin/clients', anyPerm: ['clients.view.all', 'clients.view.own'] },
      { icon: AlertTriangle, label: 'Fiches à compléter', path: '/admin/clients/completeness', anyPerm: ['customers.view', 'customers.edit', 'users.edit'] },
    ] },
    { label: 'Flotte & tiers', items: [
      { icon: UserCheck, label: 'Chauffeurs', path: '/admin/drivers', perm: 'fleet.view' },
      { icon: TruckIcon, label: 'Véhicules', path: '/admin/vehicles', perm: 'fleet.view' },
      { icon: Container, label: 'Transporteurs', path: '/admin/transport-partners', perm: 'transport_partners.view' },
    ] },
    { label: 'Analyse', items: [
      { icon: FileText, label: 'Rapports', path: '/admin/rapports', perm: 'reports.view_dashboard' },
      { icon: BookOpen, label: 'Documentation', path: '/docs/admin', perm: null, external: true },
    ] },
    { label: 'Administration', items: [
      { icon: UserCog, label: 'Staff interne', path: '/admin/internal-users', perm: 'staff.manage' },
    ] },
  ];

  const dashboardItem = { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard', perm: null };

  const allSettingsMenuItems = [
    { icon: Settings, label: 'Facturation', path: '/admin/settings?tab=payment', perm: 'settings.view' },
    { icon: Bell, label: 'Modèles notif.', path: '/admin/notifications/templates', perm: 'settings.edit' },
    { icon: Database, label: 'Sauvegardes', path: '/admin/backup', perm: 'settings.manage_backups' },
    { icon: Users, label: 'Gestion utilisateurs', path: '/admin/unified-users', badge: 'accessRequests', perm: 'users.view.all' },
    { icon: Shield, label: 'Gestion droits', path: '/admin/roles', perm: 'roles.view' },
    { icon: Eye, label: 'Audit RBAC', path: '/admin/rbac-audit', perm: 'roles.view' },
    { icon: ShieldAlert, label: 'Incidents sécurité', path: '/admin/security-incidents', perm: 'roles.view' },
    { icon: Cpu, label: 'Serveur MCP', path: '/admin/mcp', perm: 'mcp.access' },
    { icon: Target, label: 'Objectifs', path: '/admin/settings?tab=objectives', perm: 'settings.view' },
  ];

  // Filtre selon permissions (admin = bypass, sinon check perm/anyPerm)
  const canSee = (item) => {
    if (permsLoading) return false;
    if (item.adminOrFinanceOrLead) {
      return isAdmin || user?.role === 'finance' || user?.role === 'lead_commercial';
    }
    if (item.leadOpsOrAdmin) {
      return isAdmin || user?.role === 'lead_ops' || user?.role === 'lead_field_ops';
    }
    if (isAdmin) return true;
    if (item.perm === null) return true;
    if (item.anyPerm) return hasAnyPermission(item.anyPerm);
    if (item.perm) return hasPermission(item.perm);
    return true;
  };

  const settingsMenuItems = allSettingsMenuItems.filter(canSee);

  // ── Pilotage commercial (perso commercial/lead + business commercial) ──
  const dedupeByPath = (arr) => {
    const seen = new Set();
    return arr.filter((i) => (seen.has(i.path) ? false : seen.add(i.path)));
  };
  const isCommercialRole = user?.role === 'commercial' || user?.role === 'lead_commercial';
  const isLeadCommercial = user?.role === 'lead_commercial';
  const commercialPersonal = isLeadCommercial ? [
    // Le lead commercial gère son équipe : vue encours/recouvrement de TOUS les commerciaux
    { icon: Trophy, label: 'Mon dashboard', path: '/admin/commercial/dashboard', perm: null },
    { icon: Wallet, label: 'Encours & Recouvrement', path: '/admin/commercial/overdue', perm: null },
  ] : user?.role === 'commercial' ? [
    { icon: Trophy, label: 'Mon dashboard', path: '/admin/commercial/dashboard', perm: null },
    { icon: Wallet, label: 'Mes encours', path: '/admin/commercial/overdue', perm: null },
  ] : isAdmin ? [
    // L'admin est aussi un lead : vues d'équipe commerciale
    { icon: Wallet, label: 'Encours & Recouvrement', path: '/admin/commercial/overdue', perm: null },
  ] : [];
  const commercialBusiness = [
    { icon: Trophy, label: 'Performance commerciale', path: '/admin/commercial/dashboard', adminOrFinanceOrLead: true },
    { icon: Handshake, label: 'Négociations', path: '/admin/negotiations', perm: 'quotes.view.all' },
    { icon: FileSignature, label: 'Contrats', path: '/admin/contracts', perm: 'contracts.view.all' },
  ].filter(canSee);
  const commercialMenuItems = dedupeByPath([...commercialPersonal, ...commercialBusiness]);

  // ── Pilotage opérations (cockpit + exploitation) — gating par PERMISSION (RBAC V2) ──
  // Plus de gate par rôle en dur : chaque item décide via sa permission (admin = bypass).
  // Permet p.ex. à un lead_commercial disposant de 'operations.view.all' de voir le menu.
  const opsMenuItems = [
    { icon: Truck, label: 'Opérations', path: '/admin/operations', anyPerm: ['operations.view.all', 'operations.view.assigned'] },
    { icon: Gauge, label: 'Équipe Opérations', path: '/admin/ops/team', leadOpsOrAdmin: true },
    { icon: Navigation, label: 'Suivi', path: '/admin/tracking', perm: 'tracking.view.all' },
    { icon: ClipboardCheck, label: 'Bons signés', path: '/admin/loading-notes', anyPerm: ['loading_notes.view.all', 'loading_notes.view.assigned'] },
    { icon: Edit3, label: 'Demandes modif.', path: '/admin/transfer-modification-requests', anyPerm: ['operations.view.all', 'operations.edit.all'] },
    { icon: AlertTriangle, label: 'Incidents', path: '/admin/incidents', perm: 'incidents.view.all' },
  ].filter(canSee);

  // Chemins déjà en sections pilotage → exclus des groupes métier (anti-doublon)
  const pilotagePaths = new Set([dashboardItem.path, ...commercialMenuItems, ...opsMenuItems].map((i) => i.path || i));

  // Sections métier visibles (filtrées par permission + dédupliquées, sous-menus inclus)
  const visibleMainSections = mainSections
    .map((s) => ({
      ...s,
      items: s.items
        .map((i) => (i.children
          ? { ...i, children: i.children.filter((c) => canSee(c) && !pilotagePaths.has(c.path)) }
          : i))
        .filter((i) => (i.children ? i.children.length > 0 : (canSee(i) && !pilotagePaths.has(i.path)))),
    }))
    .filter((s) => s.items.length > 0);

  const showSettingsSection = settingsMenuItems.length > 0;

  useEffect(() => {
    if (user?.role === 'admin') {
      const fetchPending = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/access-requests/stats`,
            {}
          );
          setPendingAccessRequests(res.data.pending_approval || 0);
        } catch {
        // Silently ignore (already protected by parent admin role)
      }
      };
      fetchPending();
      const interval = setInterval(fetchPending, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.role]);
  
  const isSettingsActive = () => {
    return settingsMenuItems.some(item => isActive(item.path));
  };

  const renderMenuItem = (item) => {
    const Icon = item.icon;
    // Item "bientôt" (ex. Rapprochement Banque) : non cliquable
    if (item.soon) {
      return (
        <div
          key={item.label}
          title={`${item.label} — bientôt disponible`}
          className={cn(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600 cursor-not-allowed select-none',
            collapsed && 'justify-center px-0'
          )}
          data-testid={`sidebar-soon-${item.label}`}
        >
          <Icon className="w-5 h-5 flex-shrink-0 text-gray-600" />
          {!collapsed && (
            <>
              <span className="font-medium text-sm">{item.label}</span>
              <span className="ml-auto text-[9px] font-semibold uppercase tracking-wide bg-gray-700/60 text-gray-400 px-1.5 py-0.5 rounded">bientôt</span>
            </>
          )}
        </div>
      );
    }
    // Groupe dépliable (ex. Rapprochement > Wave)
    if (item.children) {
      // Sidebar repliée : afficher directement les enfants (icônes)
      if (collapsed) return item.children.map(renderMenuItem);
      const groupActive = item.children.some((c) => isActive(c.path, c.exact));
      const isOpen = openGroups[item.label] ?? groupActive;
      // Badge agrégé sur le parent Wave (visible même groupe replié)
      const childBadgeSum = item.children.reduce((acc, child) => {
        if (child.badge === 'wavePending') return acc + (wavePending?.total || 0);
        return acc;
      }, 0);
      return (
        <React.Fragment key={item.label}>
          <button
            onClick={() => setOpenGroups((prev) => ({ ...prev, [item.label]: !isOpen }))}
            data-testid={`sidebar-group-${item.label}`}
            className={cn(
              'flex items-center justify-between w-full px-3 py-2.5 rounded-lg transition-all duration-200 group',
              groupActive ? 'bg-red-600/10 text-red-400' : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
            )}
          >
            <div className="flex items-center gap-3">
              <Icon className={cn('w-5 h-5 flex-shrink-0', groupActive ? 'text-red-400' : 'text-gray-400 group-hover:text-white')} />
              <span className="font-medium text-sm">{item.label}</span>
              {childBadgeSum > 0 && !isOpen && (
                <span className="bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 px-1 flex items-center justify-center" data-testid="sidebar-wave-group-badge" title={`${wavePending.merchantUnlinked} merchants + ${wavePending.agentUnclassified} agents à traiter`}>
                  {childBadgeSum > 99 ? '99+' : childBadgeSum}
                </span>
              )}
            </div>
            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          {isOpen && (
            <div className="space-y-1 mt-1">
              {item.children.map(renderSubMenuItem)}
            </div>
          )}
        </React.Fragment>
      );
    }
    const active = isActive(item.path, item.exact);
    const isMessaging = item.badge === 'unreadMessages';
    const badgeCount = isMessaging ? unreadMessages : 0;
    // Lien externe : nouvel onglet (ex. Documentation)
    const LinkComp = item.external ? 'a' : Link;
    const linkProps = item.external
      ? { href: item.path, target: '_blank', rel: 'noopener noreferrer' }
      : { to: item.path };
    return (
      <LinkComp
        key={item.path}
        {...linkProps}
        onClick={onMobileClose}
        title={collapsed ? `${item.label}${isMessaging && badgeCount > 0 ? ` (${unreadAssigned} assignées · ${unreadOthers} autres)` : ''}` : undefined}
        className={cn(
          'relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group',
          collapsed && 'justify-center px-0',
          active
            ? 'bg-red-600/20 text-red-400 shadow-md backdrop-blur-sm border border-red-600/30'
            : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
        )}
      >
        <Icon className={cn('w-5 h-5 flex-shrink-0', active ? 'text-red-400' : 'text-gray-400 group-hover:text-white')} />
        {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
        {/* Badges :
            - Messaging : 2 badges (rouge = assignées à moi, gris = autres)
            - Autre item avec badge custom : un seul badge rouge */}
        {!collapsed && isMessaging && badgeCount > 0 && (
          <div className="ml-auto flex items-center gap-1">
            {unreadAssigned > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 px-1 flex items-center justify-center" title="Conversations assignées à moi" data-testid="sidebar-unread-assigned-badge">
                {unreadAssigned > 9 ? '9+' : unreadAssigned}
              </span>
            )}
            {unreadOthers > 0 && (
              <span className="bg-slate-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 px-1 flex items-center justify-center" title="Conversations non assignées" data-testid="sidebar-unread-others-badge">
                {unreadOthers > 9 ? '9+' : unreadOthers}
              </span>
            )}
          </div>
        )}
        {!collapsed && !isMessaging && badgeCount > 0 && (
          <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 px-1 flex items-center justify-center">
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
        {collapsed && badgeCount > 0 && (
          <span className="absolute top-0.5 right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-3.5 px-1 flex items-center justify-center" data-testid="sidebar-unread-messages-badge">
            {badgeCount > 9 ? '9+' : badgeCount}
          </span>
        )}
      </LinkComp>
    );
  };

  const renderSubMenuItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path, item.exact);
    let badgeCount = 0;
    let badgeTone = 'bg-red-500';
    let badgeTestId;
    if (item.badge === 'accessRequests') {
      badgeCount = pendingAccessRequests;
      badgeTestId = 'sidebar-access-badge';
    } else if (item.badge === 'wavePending') {
      badgeCount = wavePending?.total || 0;
      badgeTone = 'bg-amber-500';
      badgeTestId = 'sidebar-wave-pending-badge';
    }
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onMobileClose}
        title={item.badge === 'wavePending' && badgeCount > 0 ? `${wavePending.merchantUnlinked} merchants + ${wavePending.agentUnclassified} agents à traiter` : undefined}
        className={cn(
          'flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 group ml-2',
          active
            ? 'bg-amber-600/10 text-amber-400'
            : 'text-gray-500 hover:bg-gray-700/30 hover:text-gray-300'
        )}
      >
        <Icon className={cn('w-4 h-4 flex-shrink-0', active ? 'text-amber-400' : 'text-gray-500 group-hover:text-gray-300')} />
        {!collapsed && <span className="text-sm">{item.label}</span>}
        {!collapsed && badgeCount > 0 && (
          <span className={cn('ml-auto text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1', badgeTone)} data-testid={badgeTestId}>
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </Link>
    );
  };

  const getSidebarTitle = () => {
    switch (user?.role) {
      case 'admin': return 'Administration';
      case 'lead_ops': return 'Lead Opérations';
      case 'ops':
      case 'operateur': return 'Espace Opérateur';
      case 'lead_field_ops': return 'Lead Terrain';
      case 'field_ops': return 'Opérations Terrain';
      case 'lead_commercial': return 'Lead Commercial';
      case 'commercial': return 'Espace Commercial';
      case 'finance': return 'Finance / Trésorerie';
      case 'auditeur': return 'Espace Audit';
      default: return 'Dashboard';
    }
  };

  const sidebarContent = (
    <>
      {/* Logo + Toggle */}
      <div className="p-4 border-b border-gray-700 flex items-center justify-between">
        <Link to="/admin/dashboard" onClick={onMobileClose} className={cn("flex items-center gap-3", collapsed && "justify-center w-full")}>
          <div className="bg-red-600/20 p-2 rounded-lg backdrop-blur-sm flex-shrink-0">
            <Shield className="w-5 h-5 text-red-500" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <h1 className="text-base font-bold text-white truncate">{getSidebarTitle()}</h1>
              <p className="text-xs text-gray-400 font-medium">Baobab Connect</p>
            </div>
          )}
        </Link>
        {/* Close on mobile, collapse on desktop */}
        {!collapsed && (
          <button
            onClick={mobileOpen ? onMobileClose : onToggle}
            data-testid="sidebar-collapse-btn"
            className="text-gray-400 hover:text-white transition-colors p-1 rounded hover:bg-gray-700/50 flex-shrink-0"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        )}
      </div>

      {/* Expand button when collapsed (desktop only) */}
      {collapsed && !mobileOpen && (
        <button
          onClick={onToggle}
          data-testid="sidebar-expand-btn"
          className="mx-auto mt-2 text-gray-400 hover:text-white transition-colors p-2 rounded hover:bg-gray-700/50"
        >
          <PanelLeft className="w-4 h-4" />
        </button>
      )}

      {/* Menu Items */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto scrollbar-hide">
        {/* Dashboard (toujours en tête) */}
        {canSee(dashboardItem) && renderMenuItem(dashboardItem)}
        <div className="border-t border-gray-700 my-3" />

        {/* Pilotage commercial */}
        {commercialMenuItems.length > 0 && (
          <>
            {!collapsed && (
              <p className="px-4 py-2 text-xs font-semibold text-emerald-500 uppercase tracking-wider">{isAdmin ? 'Pilotage commercial' : 'Mon Espace'}</p>
            )}
            {commercialMenuItems.map(renderMenuItem)}
            <div className="border-t border-gray-700 my-3" />
          </>
        )}

        {/* Pilotage opérations */}
        {opsMenuItems.length > 0 && (
          <>
            {!collapsed && (
              <p className="px-4 py-2 text-xs font-semibold text-emerald-500 uppercase tracking-wider">Pilotage opérations</p>
            )}
            {opsMenuItems.map(renderMenuItem)}
            <div className="border-t border-gray-700 my-3" />
          </>
        )}

        {/* Sections métier */}
        {visibleMainSections.map((section, idx) => (
          <React.Fragment key={section.label || `sec-${idx}`}>
            {!collapsed && section.label && (
              <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">{section.label}</p>
            )}
            {section.items.map(renderMenuItem)}
            {idx < visibleMainSections.length - 1 && <div className="border-t border-gray-700 my-3" />}
          </React.Fragment>
        ))}

        {showSettingsSection && (
          <>
            <div className="border-t border-gray-700 my-3" />
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              className={cn(
                'flex items-center w-full px-3 py-2.5 rounded-lg transition-all duration-200 group',
                collapsed ? 'justify-center' : 'justify-between',
                isSettingsActive() ? 'bg-amber-600/10 text-amber-400' : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
              )}
              title={collapsed ? 'Paramètres' : undefined}
            >
              <div className="flex items-center gap-3">
                <Settings className={cn('w-5 h-5 flex-shrink-0', isSettingsActive() ? 'text-amber-400' : 'text-gray-400 group-hover:text-white')} />
                {!collapsed && <span className="font-medium text-sm">Paramètres</span>}
              </div>
              {!collapsed && (settingsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
            </button>
            {settingsOpen && !collapsed && (
              <div className="space-y-1 mt-1">
                {settingsMenuItems.map(renderSubMenuItem)}
              </div>
            )}
          </>
        )}
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-gray-700">
        <button
          onClick={logout}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-red-600/10 hover:text-red-400 transition-all duration-200 w-full group",
            collapsed && "justify-center px-0"
          )}
          title={collapsed ? 'Déconnexion' : undefined}
        >
          <LogOut className="w-5 h-5 text-gray-400 group-hover:text-red-400 flex-shrink-0" />
          {!collapsed && <span className="font-medium text-sm">Déconnexion</span>}
        </button>
      </div>
    </>
  );

  // ----- Mobile UX: ESC close, body scroll lock, swipe-to-close -----
  useEffect(() => {
    if (!mobileOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onMobileClose && onMobileClose(); };
    document.addEventListener('keydown', handleKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen, onMobileClose]);

  const touchStartX = React.useRef(null);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchMove = (e) => {
    if (touchStartX.current == null) return;
    if (e.touches[0].clientX - touchStartX.current < -80) {
      onMobileClose && onMobileClose();
      touchStartX.current = null;
    }
  };
  const handleTouchEnd = () => { touchStartX.current = null; };

  return (
    <>
      {/* Mobile overlay backdrop with fade */}
      <div
        onClick={onMobileClose}
        aria-hidden={!mobileOpen}
        className={cn(
          "fixed inset-0 bg-black/60 z-40 lg:hidden transition-opacity duration-200",
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
      />

      {/* Sidebar - mobile overlay or desktop fixed */}
      <aside
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={cn(
          "bg-gradient-to-b from-gray-900 to-gray-800 h-screen fixed left-0 top-0 flex flex-col shadow-xl z-50",
          "transition-transform duration-300 ease-out",
          // Mobile: hidden by default, slide in when open
          mobileOpen ? "translate-x-0 w-[min(280px,85vw)]" : "-translate-x-full w-[min(280px,85vw)]",
          // Desktop: always visible
          "lg:translate-x-0",
          !mobileOpen && (collapsed ? "lg:w-16" : "lg:w-64")
        )}>
        {sidebarContent}
      </aside>
    </>
  );
};

export default AdminSidebar;
